<template>
  <KaizenDrawer
    id="notifications-drawer"
    :show="show"
    title="Notifications"
    :subtitle="unreadCount > 0 ? `You have ${unreadCount} unread notifications` : 'You\'re all caught up'"
    width="450px"
    icon-color="primary"
    :show-mask="false"
    @close="$emit('update:show', false)"
  >
    <template #icon>
      <MaterialNotificationsIcon />
    </template>

    <!-- Flex column so the virtualized list fills the remaining drawer height
         (virtua needs a bounded-height scroll container to virtualize). -->
    <div class="notif-root">
      <!-- Filter buttons + mark-all -->
      <div class="notif-toolbar">
        <div class="notif-filters">
          <HLButton :variant="filter === 'unread' ? 'primary' : 'secondary'" :color="filter === 'unread' ? 'primary' : 'gray'" size="xs" @click="setFilter('unread')">
            Unread
          </HLButton>
          <HLButton :variant="filter === 'all' ? 'primary' : 'secondary'" :color="filter === 'all' ? 'primary' : 'gray'" size="xs" @click="setFilter('all')">All</HLButton>
        </div>
        <HLButton v-if="unreadCount > 0" variant="text" color="primary" size="xs" @click="store.markAllRead()">Mark all as read</HLButton>
      </div>

      <!-- Loading (first page): skeleton placeholders mirroring the item layout. -->
      <ul v-if="loading" class="notif-list">
        <li v-for="i in 15" :key="`skeleton-${i}`" class="notif-item notif-item--skeleton">
          <HLSkeleton :id="`notif-skeleton-avatar-${i}`" class="notif-skeleton__avatar" width="32px" height="32px" round />
          <div class="notif-item__body">
            <HLSkeleton :id="`notif-skeleton-line1-${i}`" height="14px" width="85%" />
            <HLSkeleton :id="`notif-skeleton-line2-${i}`" height="12px" width="40%" />
          </div>
        </li>
      </ul>

      <!-- Empty -->
      <div v-else-if="visibleItems.length === 0" class="notif-state notif-empty">
        <HLIcon size="32" class="notif-empty__icon">
          <MaterialNotificationsIcon />
        </HLIcon>
        <HLText size="sm" color="muted">
          {{ filter === 'unread' ? 'No unread notifications' : 'No notifications yet' }}
        </HLText>
      </div>

      <!-- List — virtualized so only the rows in view are mounted. virtua owns the
         scroll container (a <div>) and measures each row's real height, so the
         variable-height items (with/without a title line, wrapped messages)
         stay correctly positioned without a fixed row size. -->
      <!-- :key forces virtua to re-render when the visible set changes by
           CONTENT, not just length. virtua only watches `data.length`, so a
           realtime prepend that lands in an unchanged visible index range
           (e.g. a new SSE notification at index 0) wouldn't otherwise repaint.
           Keying on length + the head id repaints on prepend and on refetch. -->
      <VList v-else :key="listKey" :data="visibleItems" :item-size="76" role="list" style="height: calc(100vh - 220px)">
        <template #default="{ item: n, index }">
          <div class="notif-item" :class="{ 'notif-item--unread': !n.read_at }" role="listitem" @click="onItemClick(n)">
            <div class="notif-item__avatar">
              <HLAvatar size="sm" :name="n.actor_name || 'System'" :color="n.actor_id ? 'primary' : 'gray'" />
            </div>

            <div class="notif-item__body">
              <HLText size="sm" class="notif-item__message">
                <template v-for="(seg, i) in notificationMessageSegments(n)" :key="i">
                  <strong v-if="seg.bold">{{ seg.text }}</strong>
                  <template v-else>{{ seg.text }}</template>
                </template>
                <template v-if="notificationTitle(n)">:</template>
              </HLText>
              <HLEllipsis :tooltip-variant="TOOLTIP_VARIANT">
                <span v-if="notificationTitle(n)" class="notif-item__title">&ldquo;{{ notificationTitle(n) }}&rdquo;</span>
              </HLEllipsis>
              <HLTooltip trigger="hover" placement="bottom-start" :variant="TOOLTIP_VARIANT" :tooltip-style="tooltipStyle">
                <template #trigger>
                  <HLText size="xs" color="muted" class="notif-item__time">
                    {{ formatTime(n.createdAt) }}
                  </HLText>
                </template>
                <template #default>
                  <span :style="tooltipTextStyle">{{ formatExactTime(n.createdAt) }}</span>
                </template>
              </HLTooltip>
            </div>

            <!-- Trailing slot (unread only): a status dot by default that becomes a
               mark-as-read button on hover -->
            <div v-if="!n.read_at" class="notif-item__trailing">
              <span class="notif-item__dot" aria-hidden="true" />
              <button class="notif-item__mark-read" aria-label="Mark as read" title="Mark as read" @click.stop="store.markRead(n._id)">
                <HLIcon size="16">
                  <MaterialMarkEmailReadFillIcon />
                </HLIcon>
              </button>
            </div>
          </div>
          <div v-if="visibleItems.length > 0 && hasMore && index == visibleItems.length - 1" class="flex justify-center pt-1">
            <HLButton :loading="store.loadingMore" variant="secondary" color="primary" size="xs" @click="onLoadMore">Load older notifications</HLButton>
          </div>
        </template>
      </VList>
    </div>

    <!-- Footer: count + load-older, pinned to the bottom of the drawer
         (rendered in KaizenDrawer's #footer slot, outside the scroll area).
         v-if lives on the content, not the <template>, so the slot is always
         registered and KaizenDrawer's hasFooter check stays stable. -->
    <template #footer>
      <HLText v-if="visibleItems.length > 0" size="xs" color="muted">Showing {{ shownCount }} of {{ totalForFilter }}</HLText>
    </template>
    <!-- <template #footer>
    </template> -->
  </KaizenDrawer>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import { format, formatDistanceToNow } from 'date-fns'
  import { HLAvatar, HLButton, HLEllipsis, HLIcon, HLSkeleton, HLText, HLTooltip } from '@platform-ui/highrise'
  import { VList } from 'virtua/vue'
  import { MaterialNotificationsIcon, MaterialMarkEmailReadFillIcon } from '@gohighlevel/ghl-icons/24/material/rounded'
  import KaizenDrawer from '@/components/ui/KaizenDrawer.vue'
  import { TOOLTIP_VARIANT } from '@/constants/ui'
  import { useNotificationsStore } from '@/stores/notifications'
  import { notificationMessageSegments, notificationTitle, notificationRoute } from '@/utils/notificationHelpers'

  const props = defineProps<{ show: boolean }>()
  const emit = defineEmits<{ 'update:show': [boolean] }>()

  const router = useRouter()
  const store = useNotificationsStore()
  const { items, unreadCount, total, loading, hasMore } = storeToRefs(store)

  // Drawer is a quick-glance panel → default to what needs attention.
  const filter = ref<'all' | 'unread'>('unread')

  // On the Unread tab, an item that gets marked read should drop out of the list
  // immediately (reactive to markRead/markAllRead, no refetch needed).
  const visibleItems = computed(() => (filter.value === 'unread' ? items.value.filter(n => !n.read_at) : items.value))

  // "Showing 1–N of T" — T is the unread total on the Unread tab, the grand
  // total on All. N is what's actually rendered after the client-side filter.
  const totalForFilter = computed(() => (filter.value === 'unread' ? unreadCount.value : total.value))
  const shownCount = computed(() => visibleItems.value.length)

  // Remount key for VList. virtua only reacts to `data.length`, so a realtime
  // prepend whose new row falls in an already-rendered index range won't
  // repaint. Keying on the HEAD item's id repaints on prepend (head changes)
  // and on filter switch / refetch, but NOT on "load older" (append leaves the
  // head untouched), so infinite scroll keeps its position.
  const listKey = computed(() => visibleItems.value[0]?._id ?? 'empty')

  // A single item, inferred from the store — no need to import its type.
  type NotificationItem = (typeof items.value)[number]

  const formatTime = (ts: string) => formatDistanceToNow(new Date(ts), { addSuffix: true })
  // Exact timestamp shown in the tooltip on hover, e.g. "Jun 10, 2026, 2:58 PM".
  const formatExactTime = (ts: string) => format(new Date(ts), 'PPp')

  // Compact chip styling for the timestamp tooltip. Set on the popover surface
  // itself (tooltipStyle) — the tooltip teleports to a detached container, so
  // styling the inner content wrapper doesn't reliably cascade font-size.
  const tooltipStyle: Record<string, string> = {
    padding: '3px 7px',
    fontSize: '10px',
    lineHeight: '1',
    whiteSpace: 'nowrap',
  }
  // Inline on the content span itself — guaranteed to win since the tooltip
  // teleports out of this component's scoped styles.
  const tooltipTextStyle: Record<string, string> = {
    fontSize: '10px',
    lineHeight: '1',
  }

  const setFilter = (next: 'all' | 'unread') => {
    if (filter.value === next) return
    filter.value = next
    store.fetch({ filter: next })
  }

  const onItemClick = (n: NotificationItem) => {
    if (!n.read_at) store.markRead(n._id)
    const route = notificationRoute(n)
    // Navigate but keep the drawer open (it's non-modal, no backdrop).
    if (route) router.push(route)
  }

  const onLoadMore = () => store.loadMore()

  // Refresh on open so the drawer reflects the latest (and heals any drift).
  watch(
    () => props.show,
    open => {
      if (open) store.fetch({ filter: filter.value })
    }
  )
</script>

<style scoped>
  /* Give the pinned footer a divider + padding so it reads as separate from
     the scrolling list (KaizenDrawer leaves these off by default). Only when it
     actually has content — during loading/empty the slots are present but empty,
     and we don't want a stray bordered bar. */
  :deep(.kaizen-drawer-footer:has(.kaizen-drawer-footer-left > *)) {
    padding: 10px 16px;
    border-top: 1px solid var(--secondary-100, #eef0f4);
  }
  /* Fill the drawer body and lay out as a column so the toolbar stays fixed and
     the virtualized list (.notif-list) takes the remaining height. */
  .notif-root {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  .notif-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
    flex-shrink: 0;
  }
  .notif-filters {
    display: flex;
    gap: 8px;
  }

  .notif-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
  }

  .notif-empty {
    flex-direction: column;
    gap: 10px;
  }

  .notif-empty__icon {
    color: var(--gray-300, #d1d5db);
  }

  /* On the virtualized list this is virtua's scroll container; flex:1 + a zeroed
     min-height give it the bounded height it needs to virtualize. Shared with the
     loading skeleton <ul>, where list-style/margin/padding resets apply. */
  .notif-list {
    flex: 1;
    min-height: 0;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .notif-item {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 8px;
    border-radius: 8px;
    cursor: pointer;
    background-color: #fff;
    transition: background-color 0.15s ease;
  }

  .notif-item__avatar {
    position: relative;
    flex-shrink: 0;
  }

  .notif-item:hover {
    background-color: var(--primary-25, #f5f8ff);
  }

  /* Trailing slot: holds the unread dot and the mark-as-read button in the same
   fixed-width spot on the right. The dot shows by default; on hover it's hidden
   and the button takes its place. */
  .notif-item__trailing {
    position: relative;
    flex-shrink: 0;
    align-self: center;
    width: 28px;
    height: 28px;
  }

  .notif-item__dot {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--primary-500, #2f6fed);
    transition: opacity 0.15s ease;
  }

  .notif-item:hover .notif-item__dot {
    opacity: 0;
  }

  .notif-item__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .notif-item__message {
    line-height: 1.4;
  }

  /* Preview line showing the specific entity title in the primary color,
   truncated to one line with an ellipsis. The full text is revealed via the
   native `title` attribute on hover. :deep() so color/truncation reach the
   inner text element HLText renders. */
  .notif-item__title,
  .notif-item__title :deep(*) {
    color: var(--primary-600, #1d4ed8);
    font-style: italic;
    font-size: var(--hr-font-size-sm);
    line-height: var(--hr-line-height-sm);
    font-weight: var(--hr-font-weight-medium);
  }

  /* Tooltip wraps the timestamp; keep the trigger inline so it hugs the text and
   the relative time still reads as a normal muted caption. */
  .notif-item__time {
    display: inline-block;
    width: fit-content;
    cursor: default;
  }

  /* Skeleton placeholders reuse the item layout but aren't interactive. */
  .notif-item--skeleton {
    cursor: default;
  }

  .notif-item--skeleton:hover {
    background-color: #fff;
  }

  .notif-item--skeleton .notif-item__body {
    gap: 8px;
    padding-top: 4px;
  }

  .notif-item__mark-read {
    position: absolute;
    inset: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 1px solid var(--secondary-100, #eef0f4);
    border-radius: 6px;
    background: #fff;
    color: var(--gray-500, #6b7280);
    cursor: pointer;
    opacity: 0;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
    transition:
      opacity 0.15s ease,
      color 0.15s ease,
      border-color 0.15s ease;
  }

  .notif-item:hover .notif-item__mark-read {
    opacity: 1;
  }

  .notif-item__mark-read:hover {
    color: var(--primary-600, #1d4ed8);
    border-color: var(--primary-200, #c7d7fe);
  }
</style>
